import React, { useState } from "react";
import { Lock, Search, ArrowUpRight, Maximize2 } from "lucide-react";
import { SECTIONS } from "@/constants/testIds";
import MapLightbox from "./MapLightbox";

const MAPS = [
  {
    title: "Tagalaka Country",
    region: "Gulf Country, QLD",
    description:
      "Birds, plants and landform layers — woodland to wetland transitions.",
    stats: ["28 species", "5 landform zones", "Bush · Cultural · Soils"],
    url: "https://customer-assets.emergentagent.com/job_5f66e06f-babb-477d-81df-046e9cca7dda/artifacts/5vy8v1v4_bush%20mapping%20-%20.png",
  },
  {
    title: "Daintree Country",
    region: "Wet Tropics, QLD",
    description:
      "Canopy to coast — mountains, rainforest, rivers and reef in one connected story.",
    stats: ["60+ species", "Mountains → Reef", "4 vertical layers"],
    url: "https://customer-assets.emergentagent.com/job_5f66e06f-babb-477d-81df-046e9cca7dda/artifacts/tvbwxrm5_DAINTREE%20BUSH%20MAPPING%20FINAL%20VERSION.png",
  },
  {
    title: "Ngaanyatjarra Central Ranges",
    region: "Central Australia · IPA",
    description:
      "Songlines, sacred sites, threatened species and desert Country across the Gibson, Great Sandy and Great Victoria Deserts.",
    stats: ["IPA", "Songlines · Tjukurrpa", "Threatened species"],
    url: "https://customer-assets.emergentagent.com/job_indigenous-bush-maps/artifacts/c039bd6a_NGAANYATJARRA%20CENTRAL%20RANGES%20INDIGENOUS%20PROTECTED%20AREA.png",
  },
  {
    title: "Wuthathi Country",
    region: "Cape York · Sea Country",
    description:
      "Living landscape of land, sea and culture — from white sand dunes to coral cays and the deep ocean.",
    stats: ["117+ plant species", "Land · Sea · Sky", "Cultural burning"],
    url: "https://customer-assets.emergentagent.com/job_indigenous-bush-maps/artifacts/euvgr0vf_wuthathi%20ipa%20bush%20map.png",
  },
  {
    title: "Ngaanyatjarra Lands — Bush Foods & Botanicals",
    region: "Western Desert · Bush Tucker",
    description:
      "Bush foods, medicines and seasonal knowledge across the vast Ngaanyatjarra desert Country.",
    stats: ["35+ bush foods", "Seasonal calendar", "Language names"],
    url: "https://customer-assets.emergentagent.com/job_indigenous-bush-maps/artifacts/m4qss5g8_bush%20foods%20and%20botanicals%20of%20the%20Ngaanyatjarra%20lands.png",
  },
  {
    title: "Warrgamay Country Bush Map",
    region: "North Queensland",
    description:
      "Ranges, rivers, woodlands and open plains west of Cairns — bush foods, botanicals, animals and caring for Country practices.",
    stats: ["Bush foods", "Threatened species", "Cultural sites"],
    url: "https://customer-assets.emergentagent.com/job_indigenous-bush-maps/artifacts/m8fwet42_warrgamay%20country%20bush%20map.png",
  },
  {
    title: "Warrgamay Country Carbon Project",
    region: "Carbon · Climate · Country",
    description:
      "Caring for Country, culture and climate — Indigenous on-Country activities that build carbon, biodiversity and cultural resilience.",
    stats: ["Carbon", "Cultural burning", "Threatened species"],
    url: "https://customer-assets.emergentagent.com/job_indigenous-bush-maps/artifacts/s8x8a6tv_on%20country%20carbon%20project.png",
  },
];

export default function ExampleMaps() {
  const [active, setActive] = useState(null);

  const goto = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="examples"
      data-testid={SECTIONS.examples}
      className="relative py-24 md:py-32 bg-[#F3EFEA]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14">
          <div className="max-w-3xl">
            <div className="bm-eyebrow mb-6">Section 06 — Demonstration</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#2D2621]">
              Example Bush Maps
            </h2>
            <p className="mt-6 text-[#584D45] text-lg leading-relaxed">
              Each Bush Map is a wall-sized piece of work — often hundreds of
              illustrated elements woven together. Click any preview below
              to view it larger.
            </p>
          </div>
          <div className="flex items-center gap-3 text-[#7A6C63] text-sm">
            <Lock size={14} />
            <span className="tracking-wide">
              Watermarked sample previews · © Bush Maps
            </span>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {MAPS.map((m) => (
            <article
              key={m.title}
              className="group cursor-pointer"
              onClick={() => !m.comingSoon && setActive(m)}
              data-testid={`example-map-card-${m.title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="relative bm-map-frame aspect-[4/5]">
                <img
                  src={m.url}
                  alt={m.title}
                  draggable={false}
                  onContextMenu={(e) => e.preventDefault()}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="bm-map-watermark">
                  <span>Sample · © Bush Maps</span>
                </div>

                {/* Scale-conveying chip */}
                <div className="absolute top-4 left-4 flex flex-wrap gap-2 max-w-[80%]">
                  {m.stats.map((s) => (
                    <span
                      key={s}
                      className="text-[10px] tracking-[0.18em] uppercase px-2 py-1 bg-[#FDFBF7]/90 backdrop-blur text-[#2D2621] border border-[#FDFBF7]"
                    >
                      {s}
                    </span>
                  ))}
                </div>

                {m.comingSoon ? (
                  <div className="absolute bottom-4 left-4 px-3 py-1 bg-[#A95A3F] text-white text-[10px] tracking-[0.2em] uppercase">
                    Coming Soon
                  </div>
                ) : (
                  <div className="absolute bottom-4 left-4 flex items-center gap-2 px-3 py-2 bg-[#2C3C2B]/90 text-white text-[11px] tracking-[0.18em] uppercase backdrop-blur opacity-0 group-hover:opacity-100 transition-opacity">
                    <Maximize2 size={12} /> View larger
                  </div>
                )}

                {!m.comingSoon && (
                  <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-[#FDFBF7]/95 backdrop-blur flex items-center justify-center text-[#A95A3F] shadow-lg transition-transform group-hover:scale-110">
                    <Search size={16} />
                  </div>
                )}
              </div>

              <div className="mt-5">
                <div className="text-[11px] tracking-[0.24em] uppercase text-[#A95A3F] font-semibold">
                  {m.region}
                </div>
                <h3 className="font-serif-display text-2xl text-[#2D2621] mt-1 leading-tight">
                  {m.title}
                </h3>
                <p className="mt-2 text-[#584D45] text-[15px] leading-relaxed">
                  {m.description}
                </p>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-14 flex flex-col sm:flex-row items-start sm:items-center gap-6 p-8 bg-[#FDFBF7] border border-[#E2DACF]">
          <div className="flex-1">
            <h4 className="font-serif-display text-2xl text-[#2D2621]">
              Want to see a full Bush Map?
            </h4>
            <p className="mt-1 text-[#584D45]">
              Request a private, in-person preview as part of a Discovery Call.
              Full-resolution maps are only ever shared with partner organisations.
            </p>
          </div>
          <button
            onClick={() => goto("contact")}
            className="bm-btn-outline shrink-0"
            data-testid="examples-request-preview"
          >
            Request a preview <ArrowUpRight size={16} />
          </button>
        </div>
      </div>

      <MapLightbox
        open={!!active}
        map={active}
        onClose={() => setActive(null)}
      />
    </section>
  );
}
